import React, { useEffect, useMemo, useRef, useState } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { Toaster, toast } from "react-hot-toast";
import { apiCall } from "utils/apiClient";
import Pagination from "components/pagination";

const hasValue = (value) => value !== null && value !== undefined && value !== "";
const isObject = (value) => value && typeof value === "object";

const normalizeFaq = (faq) => ({
    ...faq,
    id: faq?.id ?? faq?.faq_id ?? faq?.faqId ?? "",
    category_id:
        faq?.category_id ??
        faq?.categoryId ??
        faq?.category?.id ??
        "",
    question:
        faq?.question ??
        faq?.faq_question ??
        faq?.title ??
        "",
    answer:
        faq?.answer ??
        faq?.faq_answer ??
        faq?.description ??
        "",
});

const normalizeCategory = (category) => ({
    ...category,
    id: category?.id ?? category?.category_id ?? category?.categoryId ?? "",
    name: category?.name ?? category?.category_name ?? category?.title ?? "",
    faq_count: Number(
        category?.faq_count ??
        category?.faqs_count ??
        category?.faqCount ??
        category?.count ??
        0
    ) || 0,
});

const looksLikeFaq = (item) => {
    if (!isObject(item)) return false;
    return (
        "question" in item ||
        "faq_question" in item ||
        "faqQuestion" in item ||
        "answer" in item ||
        "faq_answer" in item ||
        "faqAnswer" in item
    );
};

const findArrayInPayload = (payload, predicate) => {
    if (Array.isArray(payload)) return payload;
    if (!isObject(payload)) return [];

    const values = Object.values(payload);
    const arrays = values.filter(Array.isArray);

    if (predicate) {
        const match = arrays.find((arr) => arr.some(predicate));
        if (match) return match;
    }

    for (const value of values) {
        if (isObject(value) && !Array.isArray(value)) {
            const found = findArrayInPayload(value, predicate);
            if (found.length) return found;
        }
    }

    return arrays[0] || [];
};

const getArrayData = (payload, predicate) => findArrayInPayload(payload, predicate);

const isSuccessResponse = (result) =>
    !!result &&
    (result.status === 200 ||
        result.status === 201 ||
        result.status === 204 ||
        result.success === true);

const getEmptyFaqForm = (categoryId = "") => ({
    id: null,
    category_id: categoryId,
    service_id: "",
    question: "",
    answer: "",
    status: 1,
});

const FaqsCategory = () => {
    const categoryInputRef = useRef(null);
    const [categories, setCategories] = useState([]);
    const [faqs, setFaqs] = useState([]);
    const [name, setName] = useState("");
    const [editingId, setEditingId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [recordsPerPage, setRecordsPerPage] = useState(10);
    const [isFaqModalOpen, setIsFaqModalOpen] = useState(false);
    const [activeCategoryId, setActiveCategoryId] = useState("");
    const [faqPage, setFaqPage] = useState(1);
    const [faqPerPage, setFaqPerPage] = useState(10);
    const [modalView, setModalView] = useState("list");
    const [faqForm, setFaqForm] = useState(getEmptyFaqForm());

    const fetchCategories = async () => {
        try {
            const result = await apiCall({
                endpoint: "faqs-category/with-count",
                method: "GET"
            });
            if (isSuccessResponse(result)) {
                const rawCategories = getArrayData(
                    result.data ?? result,
                    (item) => isObject(item) && ("name" in item || "category_name" in item || "title" in item)
                );
                const normalized = rawCategories.map(normalizeCategory).filter((cat) => hasValue(cat.id));
                setCategories(normalized);
            } else {
                toast.error("Failed to fetch FAQ categories");
            }
        } catch {
            toast.error("Something went wrong");
        }
    };

    const fetchFaqs = async () => {
        try {
            const result = await apiCall({
                endpoint: "faqs",
                method: "GET",
            });

            if (isSuccessResponse(result)) {
                const rawFaqs = getArrayData(result.data ?? result, looksLikeFaq);
                setFaqs(rawFaqs.map(normalizeFaq).filter((faq) => hasValue(faq.id)));
            } else {
                setFaqs([]);
            }
        } catch (error) {
            console.error("Failed to fetch FAQs:", error);
            setFaqs([]);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!name.trim()) return toast.error("Category name is required");

        setLoading(true);

        const method = editingId ? "PUT" : "POST";
        const endpoint = editingId
            ? `/faqs-category/${editingId}`
            : "faqs-category";

        const result = await apiCall({
            endpoint,
            method,
            data: { name }
        });

        if (result && (result.status === 200 || result.status === 201)) {
            toast.success(`Category ${editingId ? "updated" : "added"} successfully`);
            setName("");
            setEditingId(null);
            fetchCategories();
        } else {
            toast.error("Failed to save category");
        }

        setLoading(false);
    };

    const handleEdit = (category) => {
        setName(category.name);
        setEditingId(category.id);
        requestAnimationFrame(() => {
            if (!categoryInputRef.current) return;
            categoryInputRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
            categoryInputRef.current.focus();
            categoryInputRef.current.select();
        });
    };

    const handleDeleteCategory = async (categoryId) => {
        if (!categoryId) return;
        if (!window.confirm("Delete this category?")) return;

        const result = await apiCall({
            endpoint: `/faqs-category/${categoryId}`,
            method: "DELETE",
        });

        if (!isSuccessResponse(result)) {
            toast.error("Failed to delete category");
            return;
        }

        toast.success("Category deleted successfully");
        if (String(editingId || "") === String(categoryId)) {
            handleCancel();
        }
        if (String(activeCategoryId || "") === String(categoryId)) {
            setIsFaqModalOpen(false);
            setActiveCategoryId("");
        }
        fetchCategories();
        fetchFaqs();
    };

    const handleOpenCategory = (categoryId) => {
        setActiveCategoryId(String(categoryId));
        setFaqPage(1);
        setModalView("list");
        setFaqForm(getEmptyFaqForm(String(categoryId)));
        setIsFaqModalOpen(true);
    };

    const closeModal = () => {
        setIsFaqModalOpen(false);
        setModalView("list");
        setFaqForm(getEmptyFaqForm());
        setActiveCategoryId("");
    };

    const handleFaqChange = (e) => {
        setFaqForm({ ...faqForm, [e.target.name]: e.target.value });
    };

    const handleFaqSubmit = async (e) => {
        e.preventDefault();

        const payload = {
            ...faqForm,
            service_id: "",
            category_id: faqForm.category_id || activeCategoryId || "",
        };

        if (!payload.category_id) {
            toast.error("Select category first");
            return;
        }

        const method = payload.id ? "PUT" : "POST";
        const endpoint = payload.id ? `/faqs/${payload.id}` : "faqs";

        const result = await apiCall({
            endpoint,
            method,
            data: payload,
        });

        if (isSuccessResponse(result)) {
            toast.success(`FAQ ${payload.id ? "updated" : "added"} successfully`);
            setFaqForm(getEmptyFaqForm(payload.category_id));
            setModalView("list");
            fetchFaqs();
            return;
        }

        toast.error("Operation failed");
    };

    const handleAddFaq = () => {
        setFaqForm(getEmptyFaqForm(activeCategoryId));
        setModalView("form");
    };

    const handleEditFaq = (faq) => {
        const nextCategoryId = String(faq.category_id || activeCategoryId || "");
        setActiveCategoryId(nextCategoryId);
        setFaqForm({
            id: faq.id,
            category_id: nextCategoryId,
            service_id: "",
            question: faq.question || "",
            answer: faq.answer || "",
            status: faq.status ?? 1,
        });
        setModalView("form");
        setIsFaqModalOpen(true);
    };

    const handleFaqFormCancel = () => {
        setModalView("list");
        setFaqForm(getEmptyFaqForm(activeCategoryId));
    };

    const handleDeleteFaq = async (faqId) => {
        if (!faqId) return;
        if (!window.confirm("Delete this FAQ?")) return;

        const result = await apiCall({
            endpoint: `/faqs/${faqId}`,
            method: "DELETE",
        });

        if (!isSuccessResponse(result)) {
            toast.error("Failed to delete FAQ");
            return;
        }

        toast.success("FAQ deleted successfully");
        if (String(faqForm.id || "") === String(faqId)) {
            setFaqForm(getEmptyFaqForm(activeCategoryId));
            setModalView("list");
        }
        fetchFaqs();
    };

    const handleCancel = () => {
        setName("");
        setEditingId(null);
    };

    useEffect(() => {
        fetchCategories();
        fetchFaqs();
    }, []);

    const totalRecords = categories.length;
    const totalPages = Math.max(1, Math.ceil(totalRecords / recordsPerPage));
    const indexOfLastRecord = page * recordsPerPage;
    const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
    const currentCategories = categories.slice(indexOfFirstRecord, indexOfLastRecord);
    const displayedCategoriesCount = Math.min(totalRecords, indexOfFirstRecord + currentCategories.length);

    const categoryFaqCounts = useMemo(() => {
        return faqs.reduce((acc, faq) => {
            if (!hasValue(faq.category_id)) return acc;
            const key = String(faq.category_id);
            acc[key] = (acc[key] || 0) + 1;
            return acc;
        }, {});
    }, [faqs]);

    const activeCategory = useMemo(
        () => categories.find((cat) => String(cat.id) === String(activeCategoryId)),
        [categories, activeCategoryId]
    );

    const activeCategoryFaqs = useMemo(() => {
        if (!activeCategoryId) return [];
        return faqs.filter((faq) => String(faq.category_id) === String(activeCategoryId));
    }, [faqs, activeCategoryId]);

    const totalFaqRecords = activeCategoryFaqs.length;
    const faqTotalPages = Math.max(1, Math.ceil(totalFaqRecords / faqPerPage));
    const faqLastIndex = faqPage * faqPerPage;
    const faqFirstIndex = faqLastIndex - faqPerPage;
    const currentFaqs = activeCategoryFaqs.slice(faqFirstIndex, faqLastIndex);
    const displayedFaqCount = Math.min(totalFaqRecords, faqFirstIndex + currentFaqs.length);

    useEffect(() => {
        if (page > totalPages) {
            setPage(totalPages);
        }
    }, [page, totalPages]);

    useEffect(() => {
        if (faqPage > faqTotalPages) {
            setFaqPage(faqTotalPages);
        }
    }, [faqPage, faqTotalPages]);

    const renderFaqForm = () => (
        <form onSubmit={handleFaqSubmit} className="space-y-4 bg-white border border-gray-200 p-4 rounded-lg shadow-sm">
            <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold text-gray-800">FAQ Details</h3>
                <button
                    type="button"
                    onClick={handleFaqFormCancel}
                    className="text-sm text-gray-500 hover:text-gray-700"
                >
                    Cancel
                </button>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Select Category</label>
                <select
                    name="category_id"
                    value={faqForm.category_id}
                    onChange={handleFaqChange}
                    className="w-full border border-gray-300 p-2 rounded text-sm"
                    required
                >
                    <option value="">-- Select Category --</option>
                    {categories.map((category) => (
                        <option key={category.id} value={category.id}>
                            {category.name}
                        </option>
                    ))}
                </select>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">FAQ Question</label>
                <input
                    type="text"
                    name="question"
                    value={faqForm.question}
                    onChange={handleFaqChange}
                    placeholder="Enter FAQ Question"
                    className="w-full border border-gray-300 p-2 rounded text-sm"
                    required
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">FAQ Answer</label>
                <textarea
                    name="answer"
                    value={faqForm.answer}
                    onChange={handleFaqChange}
                    placeholder="Enter FAQ Answer"
                    className="w-full border border-gray-300 p-2 rounded text-sm"
                    rows="3"
                    required
                />
            </div>

            <div>
                <button
                    type="submit"
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded text-sm transition"
                >
                    {faqForm.id ? "Update FAQ" : "Add FAQ"}
                </button>
            </div>
        </form>
    );

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <Toaster position="top-center" />
            <div className="px-6 py-5 bg-white min-h-screen">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                    <h2 className="text-lg font-bold">FAQ Categories</h2>
                </div>

                <form
                    onSubmit={handleSubmit}
                    className="border border-gray-200 rounded-lg p-3 bg-gray-50 mb-4"
                >
                    <div className="flex flex-wrap items-end gap-3">
                        <div className="min-w-[220px] flex-1">
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Category Name
                            </label>
                            <input
                                ref={categoryInputRef}
                                type="text"
                                placeholder="Enter category name"
                                className="w-full border border-gray-300 px-3 py-2 rounded text-sm"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                            />
                        </div>
                        <button
                            type="submit"
                            className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded text-sm"
                            disabled={loading}
                        >
                            {loading ? "Saving..." : editingId ? "Update" : "Add"}
                        </button>
                        {editingId && (
                            <button
                                type="button"
                                className="text-sm text-gray-600 hover:text-gray-800"
                                onClick={handleCancel}
                            >
                                Cancel
                            </button>
                        )}
                    </div>
                </form>

                <div className="border rounded-lg overflow-hidden">
                    <div className="px-4 py-3 bg-gray-50 flex items-center justify-between">
                        <h4 className="text-sm font-semibold text-gray-700">Categories</h4>
                        <span className="text-xs text-gray-500">{totalRecords} total</span>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-xs">
                            <thead className="bg-white border-b">
                                <tr>
                                    <th className="text-left px-3 py-2">Sr No</th>
                                    <th className="text-left px-3 py-2">Category</th>
                                    <th className="text-left px-3 py-2">FAQs</th>
                                    <th className="text-left px-3 py-2">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentCategories.length > 0 ? (
                                    currentCategories.map((cat, i) => {
                                        const faqCount = hasValue(cat.faq_count)
                                            ? Number(cat.faq_count || 0)
                                            : (categoryFaqCounts[String(cat.id)] || 0);
                                        return (
                                            <tr
                                                key={cat.id}
                                                className="border-t hover:bg-gray-50 cursor-pointer"
                                                onClick={() => handleOpenCategory(cat.id)}
                                            >
                                                <td className="px-3 py-2">{indexOfFirstRecord + i + 1}</td>
                                                <td className="px-3 py-2 font-medium text-gray-800">{cat.name}</td>
                                                <td className="px-3 py-2">
                                                    <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded border border-blue-200 bg-blue-50 text-blue-700">
                                                        {faqCount}
                                                    </span>
                                                </td>
                                                <td className="px-3 py-2">
                                                    <div className="flex items-center gap-3">
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleOpenCategory(cat.id);
                                                            }}
                                                            className="text-blue-600 text-xs font-medium"
                                                        >
                                                            Open
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleEdit(cat);
                                                            }}
                                                            className="text-green-600 text-xs font-medium"
                                                        >
                                                            Edit
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDeleteCategory(cat.id);
                                                            }}
                                                            className="text-red-600 text-xs font-medium"
                                                        >
                                                            Delete
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan={4} className="text-center py-4 text-gray-500">
                                            No categories found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    <div className="border-t">
                        <Pagination
                            currentPage={page}
                            totalPages={totalPages}
                            onPageChange={setPage}
                            recordsPerPage={recordsPerPage}
                            onRecordsPerPageChange={(value) => {
                                setPage(1);
                                setRecordsPerPage(value);
                            }}
                            totalRecords={totalRecords}
                            count={displayedCategoriesCount}
                        showFirstLastControls
                        compact
                        />
                    </div>
                </div>
            </div>
            {isFaqModalOpen && (
                <div
                    className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]"
                    onClick={closeModal}
                >
                    <div
                        className="bg-white rounded-lg shadow-lg w-[95%] max-w-4xl max-h-[85vh] overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="px-4 py-3 border-b flex items-center justify-between">
                            <div>
                                <h3 className="text-base font-semibold text-gray-800">
                                    FAQs - {activeCategory?.name || "Category"}
                                </h3>
                                <p className="text-xs text-gray-500">
                                    {totalFaqRecords} FAQ(s) found
                                </p>
                            </div>
                            <div className="flex items-center gap-3">
                                {modalView === "list" && (
                                    <button
                                        type="button"
                                        onClick={handleAddFaq}
                                        className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded text-xs transition"
                                    >
                                        Add FAQ
                                    </button>
                                )}
                                {modalView === "form" && (
                                    <button
                                        type="button"
                                        onClick={() => setModalView("list")}
                                        className="text-sm text-gray-500 hover:text-gray-700"
                                    >
                                        Back to List
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    className="text-gray-500 hover:text-gray-700 text-sm"
                                >
                                    Close
                                </button>
                            </div>
                        </div>

                        <div className="p-4 overflow-auto max-h-[70vh]">
                            {modalView === "form" ? (
                                renderFaqForm()
                            ) : (
                                <>
                                    <div className="border rounded overflow-x-auto">
                                        <table className="min-w-full text-xs">
                                            <thead className="bg-gray-100">
                                                <tr>
                                                    <th className="text-left px-3 py-2">Sr No</th>
                                                    <th className="text-left px-3 py-2">Question</th>
                                                    <th className="text-left px-3 py-2">Answer</th>
                                                    <th className="text-left px-3 py-2">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {currentFaqs.length > 0 ? (
                                                    currentFaqs.map((faq, i) => (
                                                        <tr key={faq.id} className="border-t">
                                                            <td className="px-3 py-2">{faqFirstIndex + i + 1}</td>
                                                            <td className="px-3 py-2">{faq.question}</td>
                                                            <td className="px-3 py-2">{faq.answer}</td>
                                                            <td className="px-3 py-2">
                                                                <div className="flex items-center gap-3">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleEditFaq(faq)}
                                                                        className="text-blue-600 text-xs"
                                                                    >
                                                                        Edit
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleDeleteFaq(faq.id)}
                                                                        className="text-red-600 text-xs"
                                                                    >
                                                                        Delete
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan={4} className="text-center py-4 text-gray-500">
                                                            No FAQs found for this category.
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className="mt-4">
                                        <Pagination
                                            currentPage={faqPage}
                                            totalPages={faqTotalPages}
                                            onPageChange={setFaqPage}
                                            recordsPerPage={faqPerPage}
                                            onRecordsPerPageChange={(value) => {
                                                setFaqPage(1);
                                                setFaqPerPage(value);
                                            }}
                                            totalRecords={totalFaqRecords}
                                            count={displayedFaqCount}
                                        showFirstLastControls
                                        compact
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </DashboardLayout>
    );
};

export default FaqsCategory;
